import { Link } from 'react-router-dom';
import { ProductImage } from '../../ui/ProductImage';
import { CartGifts } from '../cart/CartGifts';
import { useCartActions } from '../../contexts/StoreContext';
import { useCatalog } from '../../contexts/CatalogContext';
import { groupCart } from '../../core/rules/cartBundles';
import { shopLabels } from '../../data/shopLabels';
import { isSoldOut } from '../../core/rules/stock';
import { euro } from '../../core/format/currency';
import { euroCents } from './checkoutRules';
import { VoucherField } from './VoucherField';
import type { CheckoutControl } from './useCheckout';

/** De samenvatting naast het formulier: regels, cadeau, verzending en totaal. */
export function OrderSummary({
  c
}: {
  c: CheckoutControl;
}) {
  const {
    lines,
    subtotal,
    giftSubtotal,
    shipping,
    total,
    gifts,
    nextGift,
    appliedCode
  } = c.cart;
  const {
    remove
  } = useCartActions();
  const {
    routineById
  } = useCatalog();
  const {
    voucher
  } = c;
  return <aside className="lg:col-span-5" aria-labelledby="co-summary" data-id="element-7146" data-mp="2yn8j4jsqxxpb">
      <div id="co-summary-box" className="scroll-mt-24 bg-shell p-6 lg:sticky lg:top-28 lg:p-8" data-id="element-7147" data-mp="svpafzehtlz9">
        <h2 id="co-summary" className="scroll-mt-24 font-display text-xl text-ink" data-id="element-7148" data-mp="ossvcio5w802">
          Je bestelling
        </h2>
        <ul className="mt-5 divide-y divide-line border-y border-line" data-id="element-7149" data-mp="juhomgt5ydm2">
          {groupCart(lines).flatMap(g => {
          if (g.kind === 'line') return [g.line];
          // Een protocol als bundel: één kopje erboven, zelfde regels eronder (compact).
          return [{
            heading: g.routineId,
            count: g.lines.length
          } as const, ...g.lines];
        }).map(l => {
          if ('heading' in l) {
            return <li key={`bundle-${l.heading}`} className="pb-1 pt-4 text-[13px] text-clay" data-id="element-7150" data-mp="xtqtikbx5laq">
                  {routineById(l.heading)?.name ?? 'Protocol'} · {l.count} producten
                </li>;
          }
          const problem = c.lineProblem(l.product.id, isSoldOut(l.product));
          return <li key={l.product.id} className={`flex items-center gap-4 py-4 ${l.bundle ? 'pl-3' : ''}`} data-id="element-7151" data-mp="3kpqjsfbe3cbv">
                <div className="relative" data-id="element-7152" data-mp="3os3od0eeh472">
                  {/* Decoratief: de naam staat ernaast. */}
                  <ProductImage src={l.product.image} alt="" className="h-16 w-14 shrink-0 p-1.5" data-id="element-7153" data-mp="3aze00pbefn6s" />
                  <span aria-hidden="true" className="absolute -right-2 -top-2 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-ink px-1 text-[11px] text-shell" data-id="element-7154" data-mp="3fyi7nbiodcv0">
                    {l.qty}
                  </span>
                </div>
                <div className="flex-1" data-id="element-7155" data-mp="2s35q387fyo55">
                  <p className="text-[15px] leading-snug text-ink" data-id="element-7156" data-mp="2x0o2x9okxnif">
                    <span className="sr-only" data-id="element-7157" data-mp="2x0xrcstz7mm0">{shopLabels.lineQty(l.qty)} </span>
                    {l.product.name}
                  </p>
                  <p className="text-[13px] text-muted" data-id="element-7158" data-mp="v914wo69sscg">{l.subscribe ? `Bijvullen elke ${c.cart.subscriptionIntervalWeeks} weken` : l.product.brand}</p>
                  {problem && <p className="text-[13px] text-clay" data-id="element-7159" data-mp="10e4zv2cfvcgb">
                      {problem} ·{' '}
                      <button type="button" onClick={() => remove(l.product.id)} className="text-ink underline underline-offset-4" data-id="element-7160" data-mp="1e5jiv96cjdnd">
                        Weghalen<span className="sr-only" data-id="element-7161" data-mp="19ds1np92yk9w">: {l.product.name}</span>
                      </button>
                    </p>}
                </div>
                <span className="text-[15px] text-ink" data-id="element-7162" data-mp="1j4wgmoyra54q">{euroCents(l.product.price * l.qty)}</span>
              </li>;
        })}
        </ul>

        {/* Dezelfde cadeauregels als in de mand: hier kan ze het reisformaat nog wijzigen. */}
        <CartGifts gifts={gifts} compact data-id="element-7163" data-mp="1t3bx7y05pvzz" />
        {nextGift && <p className="mt-3 text-[13px] text-muted" data-id="element-7164" data-mp="2pmhuml8h39s">
            Nog {euro(nextGift.threshold - giftSubtotal)} tot {nextGift.phrase}.{' '}
            <Link to="/webshop" className="text-ink underline underline-offset-4" data-id="element-7165" data-mp="3uw04cifx93l1">
              Aanvullen
            </Link>
          </p>}

        <dl className="mt-6 space-y-2 text-[14px] text-muted" data-id="element-7166" data-mp="54jki3s8zr3b">
          <div className="flex justify-between" data-id="element-7167" data-mp="575qw03ek0jy">
            <dt data-id="element-7168" data-mp="96ehwx725kbt">Subtotaal</dt>
            <dd className="text-ink" data-id="element-7169" data-mp="9768tuppmtvr">{euroCents(subtotal)}</dd>
          </div>
          <div className="flex justify-between" data-id="element-7170" data-mp="e4odva0lwoyh">
            <dt data-id="element-7171" data-mp="e3davcezjl8f">Verzending</dt>
            <dd className={shipping === 0 ? 'text-clay' : 'text-ink'} data-id="element-7172" data-mp="3dlnspk2r2jv4">{shipping === 0 ? 'Gratis' : euroCents(shipping)}</dd>
          </div>
          {appliedCode && <div className="flex justify-between" data-id="element-7173" data-mp="3mlja46mwfb3l">
              <dt data-id="element-7174" data-mp="3mkreivtaxddb">Code</dt>
              <dd className="text-ink" data-id="element-7175" data-mp="3mjg8sqgapvjk">{appliedCode.code}</dd>
            </div>}
          {voucher.cents > 0 && <>
              <div className="flex justify-between" data-id="element-7176" data-mp="2txf2vgooipys">
                <dt data-id="element-7177" data-mp="2tusvpi5pach3">Totaal</dt>
                <dd className="text-ink" data-id="element-7178" data-mp="2yvoxnq6hrojv">{euroCents(total)}</dd>
              </div>
              <div className="flex justify-between" data-id="element-7179" data-mp="2ytlz2hr9wn3j">
                <dt data-id="element-7180" data-mp="2rb4lo0ga0o5a">Cadeaubon ···{voucher.last4}</dt>
                <dd className="text-clay" data-id="element-7181" data-mp="2mdcrt0w2mryg">−{euroCents(voucher.cents / 100)}</dd>
              </div>
            </>}
        </dl>
        <div className="mt-4 flex items-baseline justify-between border-t border-line pt-4" data-id="element-7182" data-mp="2hd8c3d2zm19a">
          <span className="text-ink" data-id="element-7183" data-mp="2ddg2qy5rrr2v">
            {voucher.cents > 0 ? 'Te betalen' : 'Totaal'} <span className="text-[13px] text-muted" data-id="element-7184" data-mp="28e3ficazkhzr">incl. btw</span>
          </span>
          <span className="font-display text-3xl text-ink" data-id="element-7185" data-mp="23n4vp2qsh1rr">{euroCents(voucher.payableCents / 100)}</span>
        </div>
        {voucher.cents > 0 && voucher.payableCents === 0 && <p className="mt-2 text-[13px] text-muted" data-id="element-7186" data-mp="1ylfhxfc6m9zz">Je cadeaubon dekt alles. Je hoeft niets meer te betalen.</p>}
        <VoucherField v={voucher} data-id="element-7187" data-mp="1pmu2vu5x5d3y" />
      </div>
    </aside>;
}